import { useEffect } from 'react';
import { Instagram, Facebook, Twitter, Mail, Phone } from 'lucide-react';

const SocialMedia = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const socialPlatforms = [
    {
      name: 'Instagram',
      handle: '@jfraifamilycatering',
      url: 'https://instagram.com/jfraifamilycatering',
      icon: <Instagram size={48} />,
      color: 'from-[#f09433] via-[#e6683c] to-[#dc2743]',
      description: 'Follow us for daily food inspiration, event highlights, and behind-the-scenes content',
      postingFrequency: '4-5 posts/week',
      contentTypes: [
        'Event photos and highlights',
        'Food close-ups and plating',
        'Behind-the-scenes kitchen prep',
        'Client testimonials and reviews',
        'Instagram Reels of food preparation',
        'Stories: Daily specials, live events'
      ]
    },
    {
      name: 'Facebook',
      handle: '@JFRAIFamilyCatering',
      url: 'https://facebook.com/jfraifamilycatering',
      icon: <Facebook size={48} />,
      color: 'from-[#1877f2] to-[#0d5dbf]',
      description: 'Connect with us on Facebook for event updates, menu specials, and community engagement',
      postingFrequency: '3-4 posts/week',
      contentTypes: [
        'Event announcements and recaps',
        'Menu specials and seasonal offerings',
        'Client reviews and testimonials',
        'Community events and partnerships',
        'Live videos from events',
        'Event planning tips and advice'
      ]
    },
    {
      name: 'Twitter / X',
      handle: '@jfraifamilycatering',
      url: 'https://twitter.com/jfraifamilycatering',
      icon: <Twitter size={48} />,
      color: 'from-[#1da1f2] to-[#0d8bd9]',
      description: 'Quick updates, event reminders, and industry news',
      postingFrequency: 'Daily',
      contentTypes: [
        'Quick event updates',
        'Last-minute availability',
        'Daily specials announcements',
        'Engagement with local businesses',
        'Food and event industry news',
        'Event planning tips'
      ]
    },
    {
      name: 'Pinterest',
      handle: '@jfraifamilycatering',
      url: 'https://pinterest.com/jfraifamilycatering',
      icon: <div className="text-4xl">📌</div>,
      color: 'from-[#e60023] to-[#bd081c]',
      description: 'Pin-worthy event ideas, menu inspirations, and decoration inspiration',
      postingFrequency: '10-15 pins/week',
      contentTypes: [
        'Event decoration ideas',
        'Menu boards and presentations',
        'Table setting inspiration',
        'Food styling and plating',
        'Event planning checklists',
        'Caribbean, Southern, American cuisine boards'
      ]
    },
    {
      name: 'TikTok',
      handle: '@jfraifamilycatering',
      url: 'https://tiktok.com/@jfraifamilycatering',
      icon: <div className="text-4xl">🎵</div>,
      color: 'from-[#00f2ea] to-[#ff0050]',
      description: 'Short-form video content, cooking trends, and viral food moments',
      postingFrequency: 'Daily',
      contentTypes: [
        'Quick recipe videos (15-60 seconds)',
        'Food prep satisfying videos',
        'Event transformation time-lapses',
        'Cooking hacks and tips',
        'Trending food challenges',
        'Day in the life content'
      ]
    }
  ];

  const hashtags = {
    primary: [
      '#JFRAICatering',
      '#JFRAIEvents',
      '#JFRAIFamily'
    ],
    catering: [
      '#CateringServices',
      '#EventCatering',
      '#WeddingCatering',
      '#CorporateCatering',
      '#PartyCatering'
    ],
    cuisine: [
      '#CaribbeanFood',
      '#SouthernCooking',
      '#SoulFood',
      '#JerkChicken',
      '#ComfortFood',
      '#AuthenticCuisine'
    ],
    events: [
      '#EventPlanning',
      '#WeddingPlanner',
      '#PartyPlanner',
      '#EventDecor',
      '#EventDesign'
    ],
    local: [
      '#[YourCity]Catering',
      '#[YourCity]Events',
      '#[YourCity]FoodScene',
      '#Local[YourCity]',
      '#Support[YourCity]Business'
    ]
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] flex items-center justify-center overflow-hidden" 
        data-testid="social-hero-section"
        style={{
          backgroundImage: 'linear-gradient(rgba(29, 161, 242, 0.85), rgba(225, 48, 108, 0.85)), url(https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="hero-content fade-in-up relative z-10">
          <h1 className="hero-title text-white drop-shadow-lg" data-testid="social-title">
            Connect With Us
          </h1>
          <div className="decorative-line bg-white"></div>
          <p className="hero-subtitle text-white drop-shadow-md" data-testid="social-subtitle">
            Follow JFRAI Family Catering on Social Media
          </p>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="section" style={{ background: 'white' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-[#8b4513] mb-4">Follow Us Everywhere</h2>
          <p className="text-lg text-[#6b4423] mb-8">
            Stay connected and never miss our latest events, menu specials, and culinary creations!
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {socialPlatforms.map((platform, index) => (
              <a
                key={index}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group"
                data-testid={`social-quick-link-${platform.name.toLowerCase()}`}
              >
                <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${platform.color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {platform.icon}
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Platform Sections */}
      {socialPlatforms.map((platform, index) => (
        <section 
          key={index}
          className="section"
          style={{ 
            background: index % 2 === 0 ? 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)' : 'white'
          }}
          data-testid={`social-platform-${platform.name.toLowerCase()}`}
        >
          <div className="max-w-6xl mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-3 gap-8 items-start`}>
              {/* Icon & Handle Side */}
              <div className="text-center lg:text-left">
                <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br ${platform.color} text-white mb-4 shadow-xl`}>
                  {platform.icon}
                </div>
                <h2 className="text-3xl font-bold text-[#8b4513] mb-2">
                  {platform.name}
                </h2>
                <p className="text-xl text-[#d2691e] font-semibold mb-4">
                  {platform.handle}
                </p>
                <a
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-block"
                  data-testid={`follow-${platform.name.toLowerCase()}-btn`}
                >
                  Follow Us
                </a>
                <p className="text-sm text-[#6b4423] mt-4">
                  <strong>Posting:</strong> {platform.postingFrequency}
                </p>
              </div>

              {/* Description & Content Types */}
              <div className="lg:col-span-2">
                <p className="text-lg text-[#6b4423] mb-6 leading-relaxed">
                  {platform.description}
                </p>
                <h3 className="text-xl font-bold text-[#8b4513] mb-3">What We Share:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {platform.contentTypes.map((type, idx) => (
                    <div key={idx} className="flex items-start">
                      <span className="text-[#d2691e] mr-2">✓</span>
                      <span className="text-[#6b4423]">{type}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Hashtags Section */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="max-w-6xl mx-auto">
          <h2 className="section-title text-white">Our Hashtags</h2>
          <div className="decorative-line bg-white"></div>
          <p className="text-center text-white mb-8">
            Use these hashtags to share your JFRAI experience or find us online!
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(hashtags).map(([category, tags], index) => (
              <div key={index} className="card bg-white/95">
                <h3 className="text-xl font-bold text-[#8b4513] mb-3 capitalize">
                  {category === 'primary' ? 'Brand Hashtags' : category === 'local' ? 'Local (Customize)' : category}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-[#d4f1f4] text-[#0abab5] rounded-full text-sm font-semibold"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Generated Content Section */}
      <section className="section" style={{ background: 'white' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="section-title">Share Your JFRAI Experience</h2>
          <div className="decorative-line"></div>
          <p className="text-lg text-[#6b4423] mb-8">
            Tag us in your posts and use <strong className="text-[#d2691e]">#JFRAICatering</strong> to be featured on our social media!
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="card">
              <div className="text-5xl mb-3">📸</div>
              <h3 className="text-xl font-bold text-[#8b4513] mb-2">Share Photos</h3>
              <p className="text-[#6b4423]">
                Post photos from your event and tag us to be featured in our gallery!
              </p>
            </div>
            <div className="card">
              <div className="text-5xl mb-3">⭐</div>
              <h3 className="text-xl font-bold text-[#8b4513] mb-2">Leave Reviews</h3>
              <p className="text-[#6b4423]">
                Share your experience on Facebook and Google to help others discover JFRAI!
              </p>
            </div>
            <div className="card">
              <div className="text-5xl mb-3">💬</div>
              <h3 className="text-xl font-bold text-[#8b4513] mb-2">Engage With Us</h3>
              <p className="text-[#6b4423]">
                Comment, like, and share our posts to stay connected with the JFRAI family!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section text-center" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
        <h2 className="section-title text-white">Prefer Direct Contact?</h2>
        <div className="decorative-line bg-white"></div>
        <p className="section-subtitle text-white mb-8">
          Reach out to us directly via phone or email
        </p>
        <div className="flex flex-wrap justify-center gap-6">
          <div className="card bg-white/95 text-left">
            <div className="flex items-center gap-3 mb-2">
              <Phone className="text-[#d2691e]" size={24} />
              <h3 className="text-lg font-bold text-[#8b4513]">Call Us</h3>
            </div>
            <p className="text-[#6b4423]">(240) 605-4915</p>
          </div>
          <div className="card bg-white/95 text-left">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="text-[#d2691e]" size={24} />
              <h3 className="text-lg font-bold text-[#8b4513]">Email Us</h3>
            </div>
            <p className="text-[#6b4423]">info@jfraifamilycatering.com</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SocialMedia;
